import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { Lock } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { ThemedText, Button, Input } from '../components';
import authService from '../api/services/authService';

// --- Animations ---

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const bubbleFloat = keyframes`
  0% {
    transform: translateY(100%) scale(0);
    opacity: 0;
  }
  10% { opacity: 1; }
  100% {
    transform: translateY(-100vh) scale(1);
    opacity: 0;
  }
`;

const pulseRing = keyframes`
  0% { transform: scale(1); opacity: 0.4; }
  50% { transform: scale(1.15); opacity: 0.8; }
  100% { transform: scale(1); opacity: 0.4; }
`;

// --- Styled Components ---

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0f1229 0%, #232562 30%, #2d3070 55%, #3a6b8a 75%, #59caee 100%);
  background-size: 300% 300%;
  animation: ${gradientShift} 12s ease infinite;
  padding: ${p => p.theme.spacing.xl};
  position: relative;
  overflow: hidden;
`;

const Bubble = styled.div`
  position: absolute;
  bottom: -20px;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, rgba(89, 202, 238, 0.2), rgba(89, 202, 238, 0.03));
  border: 1px solid rgba(89, 202, 238, 0.1);
  animation: ${bubbleFloat} ${p => p.$duration}s linear infinite;
  animation-delay: ${p => p.$delay}s;
  left: ${p => p.$left}%;
  width: ${p => p.$size}px;
  height: ${p => p.$size}px;
  pointer-events: none;
`;

const FloatingBubbles = () => {
  const bubbles = [
    { $size: 10, $left: 8, $duration: 16, $delay: 0 },
    { $size: 14, $left: 30, $duration: 13, $delay: 2 },
    { $size: 8, $left: 52, $duration: 19, $delay: 4 },
    { $size: 18, $left: 75, $duration: 14, $delay: 1 },
    { $size: 6, $left: 90, $duration: 20, $delay: 6 },
    { $size: 12, $left: 45, $duration: 17, $delay: 3 },
  ];
  return <>{bubbles.map((b, i) => <Bubble key={i} {...b} />)}</>;
};

const Card = styled.div`
  width: 100%;
  max-width: 440px;
  background: rgba(255, 255, 255, 0.07);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 20px;
  padding: 40px;
  position: relative;
  z-index: 10;
  animation: ${fadeInUp} 0.8s ease-out;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 480px) {
    padding: 28px 24px;
  }
`;

const IconCircle = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(89, 202, 238, 0.12);
  border: 1px solid rgba(89, 202, 238, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  margin-bottom: ${p => p.theme.spacing.lg};
  position: relative;
  flex-shrink: 0;

  &::after {
    content: '';
    position: absolute;
    inset: -6px;
    border-radius: 50%;
    border: 1px solid rgba(89, 202, 238, 0.15);
    animation: ${pulseRing} 3s ease-in-out infinite;
  }
`;

const LogoImage = styled.img`
  width: 110px;
  height: auto;
  margin-bottom: ${p => p.theme.spacing.lg};
  filter: brightness(0) invert(1);
  opacity: 0.9;
  display: block;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.spacing.lg};
  width: 100%;
`;

const LightLabel = styled.div`
  width: 100%;
  & label {
    color: rgba(255, 255, 255, 0.9);
  }
`;

const SuccessMessage = styled.div`
  background: rgba(16, 187, 16, 0.1);
  border: 1px solid rgba(16, 187, 16, 0.25);
  border-radius: ${p => p.theme.borderRadius.md};
  padding: ${p => p.theme.spacing.md};
  color: #6ee86e;
  text-align: center;
  font-size: ${p => p.theme.typography.sizes?.small};
`;

const ErrorMessage = styled.div`
  background: rgba(220, 53, 69, 0.1);
  border: 1px solid rgba(220, 53, 69, 0.25);
  border-radius: ${p => p.theme.borderRadius.md};
  padding: ${p => p.theme.spacing.md};
  color: #ff7b8a;
  text-align: center;
  font-size: ${p => p.theme.typography.sizes?.small};
`;

const BackLink = styled.button`
  background: none;
  border: none;
  color: rgba(89, 202, 238, 0.9);
  cursor: pointer;
  font-size: ${p => p.theme.typography.sizes?.small};
  padding: 0;
  margin-top: ${p => p.theme.spacing.md};
  align-self: center;
  font-weight: 500;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }
`;

// --- Component ---

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const theme = useTheme();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    setError('');

    try {
      await authService.forgotPassword(email);
      setSubmitted(true);
    } catch (err) {
      // Always show success message to not leak user existence
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer theme={theme}>
      <FloatingBubbles />
      <Card theme={theme}>
        <LogoImage src="/img/aqrate-logo.png" alt="aQRate Logo" />
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md, width: '100%', marginBottom: theme.spacing.xl }}>
          <IconCircle theme={theme} style={{ marginBottom: 0, flexShrink: 0 }}><Lock size={28} color="white" /></IconCircle>
          <div style={{ flex: 1 }}>
            <ThemedText type="title" style={{ fontSize: '24px', marginBottom: theme.spacing.sm, color: '#fff', fontWeight: 700, display: 'block' }}>
              Reset Password
            </ThemedText>
            <ThemedText type="default" style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', lineHeight: '1.6', display: 'block' }}>
              Enter your email address and we'll send you a link to reset your password.
            </ThemedText>
          </div>
        </div>

        {submitted ? (
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: theme.spacing.md }}>
            <SuccessMessage theme={theme} style={{ width: '100%' }}>
              If an account with that email exists, a password reset link has been sent. Please check your inbox.
            </SuccessMessage>
            <BackLink theme={theme} type="button" onClick={() => navigate('/login')}>
              Back to Sign In
            </BackLink>
          </div>
        ) : (
          <Form onSubmit={handleSubmit}>
            <LightLabel>
              <Input
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
              />
            </LightLabel>

            {error && <ErrorMessage theme={theme}>{error}</ErrorMessage>}

            <Button type="submit" size="lg" disabled={loading || !email}>
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>

            <BackLink theme={theme} type="button" onClick={() => navigate('/login')}>
              Back to Sign In
            </BackLink>
          </Form>
        )}
      </Card>
    </PageContainer>
  );
};
